import React, { useEffect, useState } from 'react';
import {
    Plus,
    RefreshCw,
    Trash2,
    Copy,
    Check,
    Eye,
    EyeOff,
    Pencil,
    Power,
    Send,
    ChevronDown,
    ChevronUp,
    History,
} from 'lucide-react';
import ENTITY_ICONS from '../components/common/entityIcons';
import webhooksService from '../services/webhooks';
import { useApps } from '../contexts/AppsContext';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import Modal from '../components/common/Modal';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { STATUS_OPTIONS } from '../components/common/filters';
import useServerTable from '../hooks/useServerTable';
import { isValidWebhookUrl, webhookUrlHintText } from '../utils/webhookUrl';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import FeatureOffState, { useFeatureGate } from '../components/common/FeatureOffState';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { TextInput } from '../components/ui/Input';

const WebhookIcon = ENTITY_ICONS.webhooks;

const DELIVERY_STATUS_OPTIONS = [
    { value: 'delivered', label: 'Delivered' },
    { value: 'failed', label: 'Failed' },
    { value: 'retrying', label: 'Retrying' },
    { value: 'pending', label: 'Pending' },
];

// A webhook turns itself off after 10 consecutive failures (the dashboard's
// "turned off" signal = Disabled + 10 or more failures).
const parseEvents = (events) => {
    if (Array.isArray(events)) return events;
    if (typeof events === 'string') {
        try {
            return JSON.parse(events);
        } catch {
            return events.split(',').map((s) => s.trim());
        }
    }
    return [];
};

const truncateUrl = (url, maxLen = 40) => {
    if (!url || url.length <= maxLen) return url;
    return url.substring(0, maxLen) + '...';
};

const EventPill = ({ children }) => (
    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-100">
        {children}
    </span>
);

const WebhooksPage = () => {
    const { loading: loadingApps, selectedAppId: appId, selectedApp } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    const gate = useFeatureGate(appId);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newSecretData, setNewSecretData] = useState(null);
    const [copiedField, setCopiedField] = useState('');
    const [showSecret, setShowSecret] = useState(false);
    const [editingWebhook, setEditingWebhook] = useState(null);
    const [expandedEvents, setExpandedEvents] = useState(null);
    const [deliveriesFor, setDeliveriesFor] = useState(null); // webhook whose delivery log modal is open
    const [formData, setFormData] = useState({
        webhook_url: '',
        events: '',
        description: '',
        tenant_id: '',
    });

    // URL keys: status, failures_min/max, last_triggered_*, created_*. Delivery log (modal): status, event, created_*.
    const columns = [
        {
            key: 'webhook_url',
            header: 'URL',
            sortKey: 'url',
            locked: true,
            render: (wh) => (
                <div className="flex items-center gap-2">
                    <div className="min-w-0">
                        <div className="font-mono text-xs text-slate-700" title={wh.webhook_url}>
                            {truncateUrl(wh.webhook_url)}
                        </div>
                        {wh.description && <div className="text-xs text-slate-500 mt-0.5">{wh.description}</div>}
                    </div>
                </div>
            ),
        },
        {
            key: 'events',
            header: 'Events',
            render: (wh) => {
                const events = parseEvents(wh.events);
                const expanded = expandedEvents === wh.webhook_id;
                const hasMore = events.length > 2;
                return (
                    <div className="flex flex-wrap gap-1 max-w-xs">
                        {(expanded ? events : events.slice(0, 2)).map((ev, i) => (
                            <EventPill key={i}>{ev}</EventPill>
                        ))}
                        {hasMore && (
                            <button
                                type="button"
                                onClick={() => setExpandedEvents(expanded ? null : wh.webhook_id)}
                                className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                            >
                                {expanded ? 'less' : `+${events.length - 2} more`}
                                {expanded ? (
                                    <ChevronUp className="h-3 w-3 ml-0.5" />
                                ) : (
                                    <ChevronDown className="h-3 w-3 ml-0.5" />
                                )}
                            </button>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'is_active',
            header: 'Status',
            sortKey: 'status',
            render: (wh) => <StatusBadge active={Boolean(wh.is_active)} />,
            filter: { key: 'status', type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'failure_count',
            header: 'Failures',
            sortKey: 'failures',
            // A webhook turns itself off after 10 consecutive failures (the dashboard's
            // "turned off" signal = Inactive + 10 or more failures).
            filter: { key: 'failures', label: 'Consecutive failures', type: 'number', defaultExpanded: true },
            render: (wh) => (
                <span className={`text-xs font-medium ${wh.failure_count > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                    {wh.failure_count || 0}
                </span>
            ),
        },
        {
            key: 'last_triggered_at',
            header: 'Last Triggered',
            sortKey: 'last_triggered',
            className: 'text-xs text-slate-500 whitespace-nowrap',
            render: (wh) => (wh.last_triggered_at ? new Date(wh.last_triggered_at).toLocaleString() : 'Never'),
            filter: { key: 'last_triggered', type: 'date' },
        },
        {
            key: 'created_on',
            header: 'Created',
            sortKey: 'created',
            defaultVisible: false,
            className: 'text-xs text-slate-500 whitespace-nowrap',
            render: (wh) => (wh.created_on ? new Date(wh.created_on).toLocaleString() : '—'),
            filter: { key: 'created', type: 'date' },
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (wh) => (
                <RowActions>
                    <RowAction icon={Pencil} label="Edit" onClick={() => openEditModal(wh)} />
                    <RowAction
                        icon={Power}
                        label={wh.is_active ? 'Deactivate webhook' : 'Activate webhook'}
                        tone={wh.is_active ? 'warning' : 'success'}
                        onClick={() => handleToggleActive(wh)}
                    />
                    <RowAction
                        icon={RefreshCw}
                        label="Rotate secret"
                        onClick={() => handleRotateSecret(wh.webhook_id)}
                    />
                    <RowAction
                        icon={Send}
                        label="Send test event"
                        tone="info"
                        onClick={() => handleTest(wh.webhook_id)}
                    />
                    <RowAction icon={History} label="View deliveries" onClick={() => setDeliveriesFor(wh)} />
                    <RowAction icon={Trash2} label="Delete" tone="danger" onClick={() => handleDelete(wh.webhook_id)} />
                </RowActions>
            ),
        },
    ];

    const deliveryColumns = [
        {
            key: 'event_type',
            header: 'Event Type',
            sortKey: 'event',
            locked: true,
            className: 'font-mono text-xs',
            filter: { key: 'event', label: 'Event', type: 'text', placeholder: 'e.g. user.created' },
        },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (d) => <StatusBadge status={d.status} title={d.error_message || undefined} />,
            filter: { type: 'multiselect', options: DELIVERY_STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'response_status',
            header: 'Response',
            sortKey: 'response',
            className: 'font-mono text-xs',
            render: (d) => d.response_status || '—',
        },
        { key: 'attempt_count', header: 'Attempts', sortKey: 'attempts', render: (d) => d.attempt_count || 1 },
        {
            key: 'created_on',
            header: 'Timestamp',
            sortKey: 'created',
            className: 'text-xs text-slate-500 whitespace-nowrap',
            render: (d) => (d.created_on ? new Date(d.created_on).toLocaleString() : '—'),
            filter: { key: 'created', label: 'Sent', type: 'date' },
        },
    ];

    const table = useServerTable((query) => gate.guard(appId, webhooksService.getPage(appId, query)), {
        deps: [appId],
        enabled: Boolean(appId),
        defaultSort: { key: 'created', dir: 'desc' },
        columns,
    });

    // The delivery log endpoint already pages: { results, pagination } under data.
    const deliveries = useServerTable(
        (query) => webhooksService.getDeliveries(deliveriesFor.webhook_id, query).then((res) => res?.data),
        {
            deps: [deliveriesFor?.webhook_id],
            enabled: Boolean(deliveriesFor),
            defaultSort: { key: 'created', dir: 'desc' },
            columns: deliveryColumns,
            urlState: false, // lives in a modal: keep the page URL for the webhooks table
        }
    );

    // Expanded event lists belong to the previous app's rows.
    useEffect(() => {
        setExpandedEvents(null);
    }, [appId]);

    const resetForm = () => {
        setFormData({ webhook_url: '', events: '', description: '', tenant_id: '' });
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setError('');
        if (!isValidWebhookUrl(formData.webhook_url)) {
            setError(webhookUrlHintText());
            return;
        }
        try {
            const payload = {
                app_id: appId,
                webhook_url: formData.webhook_url,
                events: formData.events
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
            };
            if (formData.description) payload.description = formData.description;
            if (formData.tenant_id) payload.tenant_id = formData.tenant_id;
            const res = await webhooksService.register(payload);
            setNewSecretData(res.data);
            setShowCreateModal(false);
            resetForm();
            table.reload();
            notice.success('Webhook registered. Copy its secret below — it won’t be shown again.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to register webhook');
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setError('');
        if (!isValidWebhookUrl(formData.webhook_url)) {
            setError(webhookUrlHintText());
            return;
        }
        try {
            const payload = {
                webhook_id: editingWebhook.webhook_id,
                webhook_url: formData.webhook_url,
                events: formData.events
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                description: formData.description || undefined,
            };
            await webhooksService.update(payload);
            setShowEditModal(false);
            setEditingWebhook(null);
            resetForm();
            table.reload();
            notice.success('Webhook updated.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update webhook');
        }
    };

    const openEditModal = (wh) => {
        setError('');
        setEditingWebhook(wh);
        setFormData({
            webhook_url: wh.webhook_url,
            events: parseEvents(wh.events).join(', '),
            description: wh.description || '',
            tenant_id: wh.tenant_id || '',
        });
        setShowEditModal(true);
    };

    const handleToggleActive = async (wh) => {
        const newStatus = wh.is_active ? 0 : 1;
        const action = wh.is_active ? 'deactivate' : 'activate';
        const verb = wh.is_active ? 'Deactivate' : 'Activate';
        const ok = await confirm({
            title: `${verb} webhook ${wh.webhook_url}?`,
            body: wh.is_active
                ? 'Events stop being delivered to this endpoint until it is activated again.'
                : 'Subscribed events are delivered to this endpoint again from now on.',
            confirmLabel: verb,
            tone: wh.is_active ? 'danger' : 'default',
        });
        if (!ok) return;
        try {
            await webhooksService.update({ webhook_id: wh.webhook_id, is_active: newStatus });
            table.reload();
            notice.success(`Webhook ${action}d.`);
        } catch (err) {
            notice.error(err, `Couldn’t ${action} the webhook. Try again.`);
        }
    };

    const handleRotateSecret = async (webhookId) => {
        const target = table.rows.find((w) => w.webhook_id === webhookId);
        const ok = await confirm({
            title: `Rotate the signing secret for ${target?.webhook_url || 'this webhook'}?`,
            body: 'Deliveries are signed with the new secret immediately. Signatures made with the current secret no longer verify, so update the receiving service.',
            confirmLabel: 'Rotate',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            const res = await webhooksService.rotateSecret({ webhook_id: webhookId });
            setNewSecretData(res.data);
            table.reload();
            notice.success('Secret rotated. Copy the new secret below.');
        } catch (err) {
            notice.error(err, 'Couldn’t rotate the secret. Try again.');
        }
    };

    const handleTest = async (webhookId) => {
        try {
            await webhooksService.test({ webhook_id: webhookId });
            notice.success('Test event sent.');
        } catch (err) {
            notice.error(err, 'Test event failed. Check the endpoint URL and try again.');
        }
    };

    const handleDelete = async (webhookId) => {
        const target = table.rows.find((w) => w.webhook_id === webhookId);
        const ok = await confirm({
            title: `Delete webhook ${target?.webhook_url || webhookId}?`,
            body: 'No further events are delivered to this endpoint.',
            confirmLabel: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            await webhooksService.deleteWebhook({ webhook_id: webhookId });
            table.reload();
            notice.success('Webhook deleted.');
        } catch (err) {
            notice.error(err, 'Couldn’t delete the webhook. Try again.');
        }
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(''), 2000);
    };



    return (
        <div className="space-y-4">
            <PageHeader
                title="Webhooks"
                actions={
                    appId &&
                    !gate.off && (
                        <Button
                            onClick={() => {
                                resetForm();
                                setError('');
                                setShowCreateModal(true);
                            }}
                        >
                            <Plus className="h-4 w-4" aria-hidden="true" /> Register Webhook
                        </Button>
                    )
                }
            />

            {/* New Secret Display (shown after create/rotate) */}
            {newSecretData && (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-5 space-y-3">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
                        <WebhookIcon className="h-4 w-4" aria-hidden="true" />
                        Save this secret — it won't be shown again!
                    </div>
                    <div className="space-y-2">
                        {newSecretData.webhook_id && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-500 w-24">Webhook ID:</span>
                                <code className="flex-1 bg-white rounded px-2 py-1 text-sm font-mono border border-amber-200">
                                    {newSecretData.webhook_id}
                                </code>
                                <button
                                    onClick={() => copyToClipboard(newSecretData.webhook_id, 'id')}
                                    className="p-1 rounded hover:bg-amber-100"
                                >
                                    {copiedField === 'id' ? (
                                        <Check className="h-4 w-4 text-emerald-600" />
                                    ) : (
                                        <Copy className="h-4 w-4 text-slate-500" />
                                    )}
                                </button>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500 w-24">Secret:</span>
                            <code className="flex-1 bg-white rounded px-2 py-1 text-sm font-mono border border-amber-200">
                                {showSecret ? newSecretData.secret : '\u2022'.repeat(40)}
                            </code>
                            <button
                                onClick={() => setShowSecret(!showSecret)}
                                className="p-1 rounded hover:bg-amber-100"
                            >
                                {showSecret ? (
                                    <EyeOff className="h-4 w-4 text-slate-500" />
                                ) : (
                                    <Eye className="h-4 w-4 text-slate-500" />
                                )}
                            </button>
                            <button
                                onClick={() => copyToClipboard(newSecretData.secret, 'secret')}
                                className="p-1 rounded hover:bg-amber-100"
                            >
                                {copiedField === 'secret' ? (
                                    <Check className="h-4 w-4 text-emerald-600" />
                                ) : (
                                    <Copy className="h-4 w-4 text-slate-500" />
                                )}
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setNewSecretData(null);
                            setShowSecret(false);
                        }}
                        className="text-xs text-amber-700 hover:underline"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {loadingApps ? (
                <SkeletonList />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to manage its webhooks.</p>
                </div>
            ) : gate.off ? (
                <FeatureOffState
                    feature="Webhooks"
                    plural
                    appName={selectedApp?.app_name}
                    description="Sends signed HTTPS notifications to your services when users, roles or settings change."
                />
            ) : (
                <DataTable
                    {...table.tableProps}
                    columns={columns}
                    tableKey="webhooks"
                    rowKey="webhook_id"
                    searchPlaceholder="Search URL, description, events…"
                    emptyMessage="No webhooks yet. Register one to receive event notifications."
                />
            )}

            {/* Delivery Log Modal */}
            <Modal
                isOpen={Boolean(deliveriesFor)}
                onClose={() => setDeliveriesFor(null)}
                title="Delivery Log"
                size="lg"
            >
                {deliveriesFor && (
                    <div className="space-y-3">
                        <div className="font-mono text-xs text-slate-500 break-all">{deliveriesFor.webhook_url}</div>
                        <DataTable
                            {...deliveries.tableProps}
                            columns={deliveryColumns}
                            tableKey="webhook-deliveries"
                            rowKey="delivery_id"
                            searchPlaceholder="Search event, error…"
                            emptyMessage="No deliveries recorded yet."
                        />
                    </div>
                )}
            </Modal>

            {/* Create Webhook Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Register Webhook"
                size="sm"
            >
                <form onSubmit={handleCreate} className="space-y-4">
                    <Field
                        label={
                            <>
                                Webhook URL <span className="text-red-500">*</span>
                            </>
                        }
                        hint="Must start with https://"
                    >
                        <TextInput
                            type="url"
                            required
                            value={formData.webhook_url}
                            onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                            placeholder="https://example.com/webhooks"
                        />
                    </Field>
                    <Field
                        label={
                            <>
                                Events <span className="text-red-500">*</span>
                            </>
                        }
                        hint="Comma-separated event patterns"
                    >
                        <TextInput
                            type="text"
                            required
                            value={formData.events}
                            onChange={(e) => setFormData({ ...formData, events: e.target.value })}
                            placeholder="delegation.*, group.*, user.created"
                        />
                    </Field>
                    <Field label="Description (optional)">
                        <TextInput
                            type="text"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="e.g., Production event handler"
                        />
                    </Field>
                    <Field label="Tenant (optional)">
                        <TextInput
                            type="text"
                            value={formData.tenant_id}
                            onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                            placeholder="Tenant ID"
                        />
                    </Field>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Register
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Edit Webhook Modal */}
            <Modal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setEditingWebhook(null);
                }}
                title="Edit Webhook"
                size="sm"
            >
                <form onSubmit={handleEdit} className="space-y-4">
                    <Field
                        label={
                            <>
                                Webhook URL <span className="text-red-500">*</span>
                            </>
                        }
                        hint="Must start with https://"
                    >
                        <TextInput
                            type="url"
                            required
                            value={formData.webhook_url}
                            onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                            placeholder="https://example.com/webhooks"
                        />
                    </Field>
                    <Field
                        label={
                            <>
                                Events <span className="text-red-500">*</span>
                            </>
                        }
                        hint="Comma-separated event patterns"
                    >
                        <TextInput
                            type="text"
                            required
                            value={formData.events}
                            onChange={(e) => setFormData({ ...formData, events: e.target.value })}
                            placeholder="delegation.*, group.*, user.created"
                        />
                    </Field>
                    <Field label="Description (optional)">
                        <TextInput
                            type="text"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="e.g., Production event handler"
                        />
                    </Field>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Save Changes
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowEditModal(false);
                                setEditingWebhook(null);
                            }}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default WebhooksPage;
